/**
 * Menu do botão direito sobre uma pessoa.
 *
 * É o mesmo menu na lista da direita, na lista de quem está em chamada e nos
 * quadros da sala de voz. Antes eram dois menus diferentes, e a consequência
 * prática era ter que lembrar de qual lista clicar para achar cada ação: o
 * volume só existia na chamada, a moderação só na lista de membros.
 *
 * O que aparece depende de três coisas: quem é a pessoa (você mesmo tem menos
 * opções), suas permissões no servidor, e se ela está na mesma chamada que
 * você. Item que sempre devolveria erro não é mostrado.
 */

import { useContext } from 'react'
import {
  AtSign,
  Copy,
  MicOff,
  PenLine,
  PhoneOff,
  UserMinus,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { Permission, hasPermission, type GuildMember } from '@nexus/shared'
import { api } from '@/lib/api'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { usePreferencesStore } from '@/store/preferences'
import { useVoiceStore } from '@/store/voice'
import { useGuildPermissions, useMember } from '@/hooks/selectors'
import { ContextMenu, MenuItem, MenuSeparator, Slider } from '@/components/ui'
import { MessageComposerContext } from '@/components/chat/composer-context'

export interface PosicaoDoMenu {
  x: number
  y: number
}

export function UserContextMenu({
  userId,
  guildId,
  posicao,
  /** Usado quando o membro ainda não está carregado no estado. */
  nomeDeReserva,
  onAlterarApelido,
  onClose,
}: {
  userId: string
  guildId: string
  posicao: PosicaoDoMenu
  nomeDeReserva?: string
  /** O modal de apelido vive no pai: este menu some assim que alguém clica. */
  onAlterarApelido?: (membro: GuildMember) => void
  onClose: () => void
}) {
  const myId = useAuthStore((s) => s.user?.id)
  const guild = useAppStore((s) => s.guilds[guildId])
  const membro = useMember(guildId, userId)
  const permissoes = useGuildPermissions(guildId)
  const voiceState = useAppStore((s) => s.voiceStates[userId])
  const meuCanalDeVoz = useVoiceStore((s) => s.channelId)
  const composer = useContext(MessageComposerContext)

  const volume = usePreferencesStore((s) => s.userVolumes[userId] ?? 100)
  const setUserVolume = usePreferencesStore((s) => s.setUserVolume)

  const souEu = userId === myId
  const eDono = guild?.ownerId === userId
  const nome = membro?.nickname ?? membro?.user.displayName ?? nomeDeReserva ?? 'Usuário'

  const podeExpulsar =
    !souEu && !eDono && hasPermission(permissoes, Permission.KICK_MEMBERS) && Boolean(membro)
  const podeSilenciar = !souEu && hasPermission(permissoes, Permission.MUTE_MEMBERS)
  const podeDesconectar = !souEu && hasPermission(permissoes, Permission.MOVE_MEMBERS)
  const podeMudarApelido =
    Boolean(membro) &&
    hasPermission(permissoes, souEu ? Permission.CHANGE_NICKNAME : Permission.MANAGE_NICKNAMES)

  const naChamada = Boolean(voiceState?.channelId)
  // O volume só faz sentido quando dá para ouvir a pessoa agora.
  const mostrarVolume = !souEu && naChamada && voiceState?.channelId === meuCanalDeVoz
  const silenciado = volume === 0

  return (
    <ContextMenu x={posicao.x} y={posicao.y} onClose={onClose}>
      <div className="px-2.5 py-1.5">
        <p className="truncate text-[13px] font-semibold text-content-primary">{nome}</p>
        {membro ? (
          <p className="truncate text-[11px] text-content-muted">{membro.user.username}</p>
        ) : null}
      </div>

      <MenuSeparator />

      {composer && membro ? (
        <MenuItem icon={<AtSign />} onClick={() => composer.insertText(`@${nome} `)}>
          Mencionar
        </MenuItem>
      ) : null}

      {podeMudarApelido && membro && onAlterarApelido ? (
        <MenuItem icon={<PenLine />} onClick={() => onAlterarApelido(membro)}>
          {souEu ? 'Alterar meu apelido' : 'Alterar apelido'}
        </MenuItem>
      ) : null}

      {mostrarVolume ? (
        <>
          <MenuSeparator />
          <div className="px-2 pt-1 pb-2">
            <Slider
              value={volume}
              onChange={(v) => setUserVolume(userId, v)}
              min={0}
              max={200}
              step={5}
              label="Volume"
              formatValue={(v) => `${v}%`}
            />

            <div className="mt-1 flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setUserVolume(userId, silenciado ? 100 : 0)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-surface-raised px-2 py-1.5 text-xs text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
              >
                {silenciado ? <Volume2 className="size-3.5" /> : <VolumeX className="size-3.5" />}
                {silenciado ? 'Ouvir' : 'Silenciar para mim'}
              </button>

              <button
                type="button"
                onClick={() => setUserVolume(userId, 100)}
                disabled={volume === 100}
                className="rounded-md bg-surface-raised px-2 py-1.5 text-xs text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary disabled:opacity-40"
              >
                Padrão
              </button>
            </div>
          </div>
        </>
      ) : null}

      {naChamada && (podeSilenciar || podeDesconectar) ? (
        <>
          <MenuSeparator />
          {podeSilenciar ? (
            <MenuItem
              icon={voiceState?.serverMute ? <Volume2 /> : <MicOff />}
              onClick={() =>
                void api.voice.moderate(voiceState!.channelId!, {
                  userId,
                  serverMute: !voiceState?.serverMute,
                })
              }
            >
              {voiceState?.serverMute ? 'Remover silêncio' : 'Silenciar para todos'}
            </MenuItem>
          ) : null}

          {podeDesconectar ? (
            <MenuItem
              icon={<PhoneOff />}
              danger
              onClick={() =>
                void api.voice.moderate(voiceState!.channelId!, { userId, disconnect: true })
              }
            >
              Desconectar da chamada
            </MenuItem>
          ) : null}
        </>
      ) : null}

      <MenuSeparator />
      <MenuItem icon={<Copy />} onClick={() => void navigator.clipboard.writeText(userId)}>
        Copiar ID do usuário
      </MenuItem>

      {podeExpulsar ? (
        <>
          <MenuSeparator />
          <MenuItem
            icon={<UserMinus />}
            danger
            onClick={() => void api.guilds.kickMember(guildId, userId)}
          >
            Expulsar do servidor
          </MenuItem>
        </>
      ) : null}
    </ContextMenu>
  )
}
